import { Controller } from "@hotwired/stimulus"

// Tab switching for Brand::TabsComponent and Brand::FaqGroupComponent.
//
// The server renders the selected tab with aria-selected="true" and
// tabindex="0" and hides the other panels, so the page is correct before
// this connects. This only moves that state on click and arrow keys, and
// positions the slider under the active tab where there is one.
export default class extends Controller {
  static targets = ["list", "tab", "panel", "indicator", "slider"]
  static values = { orientation: { type: String, default: "horizontal" } }

  connect() {
    this.positionSlider()
    if (typeof ResizeObserver !== "undefined" && this.hasListTarget) {
      this.resizeObserver = new ResizeObserver(() => this.positionSlider())
      this.resizeObserver.observe(this.listTarget)
    }
  }

  disconnect() {
    if (this.resizeObserver) this.resizeObserver.disconnect()
  }

  select(event) {
    this.activate(this.tabTargets.indexOf(event.currentTarget))
  }

  previous() {
    if (this.activeIndex() > 0) this.activate(this.activeIndex() - 1)
  }

  next() {
    if (this.activeIndex() < this.tabTargets.length - 1) this.activate(this.activeIndex() + 1)
  }

  keydown(event) {
    const vertical = this.orientationValue === "vertical"
    const backward = vertical ? "ArrowUp" : "ArrowLeft"
    const forward = vertical ? "ArrowDown" : "ArrowRight"
    const count = this.tabTargets.length
    const current = this.tabTargets.indexOf(event.currentTarget)
    let index

    if (event.key === backward) index = (current - 1 + count) % count
    else if (event.key === forward) index = (current + 1) % count
    else if (event.key === "Home") index = 0
    else if (event.key === "End") index = count - 1
    else return

    event.preventDefault()
    this.activate(index)
    this.tabTargets[index].focus()
  }

  activeIndex() {
    return this.tabTargets.findIndex((tab) => tab.getAttribute("aria-selected") === "true")
  }

  activate(index) {
    this.tabTargets.forEach((tab, i) => {
      const active = i === index
      tab.setAttribute("aria-selected", active ? "true" : "false")
      tab.setAttribute("tabindex", active ? "0" : "-1")
      tab.classList.toggle("Tabs__item--active", active && tab.classList.contains("Tabs__item"))
    })
    this.panelTargets.forEach((panel, i) => { panel.hidden = i !== index })
    this.indicatorTargets.forEach((dot, i) => dot.classList.toggle("Tabs__indicator--active", i === index))

    const last = index === this.tabTargets.length - 1
    this.element.classList.toggle("Tabs-container--lastTabIsActive", last)
    const wrapper = this.element.querySelector(".Tablist__wrapper")
    if (wrapper) wrapper.classList.toggle("Tablist__wrapper--lastTabIsActive", last)

    this.positionSlider()
  }

  // The slider is a bar that slides under the active tab. Its position is
  // measured from the list's own padding, which the CSS exposes as a token.
  positionSlider() {
    if (!this.hasSliderTarget || !this.hasListTarget) return
    const tab = this.tabTargets[this.activeIndex()]
    if (!tab) return

    const paddingRem = parseFloat(getComputedStyle(this.listTarget).getPropertyValue("--brand-Tabs-container-padding"))
    const padding = Number.isNaN(paddingRem) ? 0 : paddingRem * 16
    this.sliderTarget.style.setProperty("--brand-Tabs-slider-x", `${tab.offsetLeft - padding}px`)
    this.sliderTarget.style.setProperty("--brand-Tabs-slider-width", `${tab.offsetWidth}px`)
  }
}
